import React, { useState } from 'react'
import { Anchor, Button, H3, PendingIcon, Span, Image, Line } from '../../lib/components'
import { OpportunityDetails } from '../../lib/types'
import { displayDollar } from '../../lib/utils/helpers'
import { PLATFORM_WITHDRAWAL_FEE, PLATFORM_WITHDRAWAL_FEE_CURRENCY } from './constants'
import { WithdrawalNoticeModal } from './WithdrawalNoticeModal'

const WithdrawalRequestConfirmation = ({
  opportunity,
  withdrawalAmount,
  handlePlaceWithdrawalRequest,
  onWithdrawalPlacedSuccess,
}: {
  opportunity: OpportunityDetails
  withdrawalAmount: string
  handlePlaceWithdrawalRequest: () => Promise<boolean>
  onWithdrawalPlacedSuccess: () => void
}) => {
  const [showCancellationNotice, setShowCancellationNotice] = useState(false)
  const [showSuccessNotice, setShowSuccessNotice] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const getTotalAmount = (amount: string | number, fee: string | number): string => {
    return (Number(amount) - Number(fee)).toString()
  }

  const handleConfirmPlaceWithdrawalRequest = async () => {
    setIsSubmitting(true)
    const submitted = await handlePlaceWithdrawalRequest()
    setIsSubmitting(false)

    if (submitted) {
      setShowCancellationNotice(false)
      setShowSuccessNotice(true)
    }
  }

  const handleDismissSuccessNotice = () => {
    setShowSuccessNotice(false)
    onWithdrawalPlacedSuccess()
  }

  return (
    <>
      <div className='withdraw-confirmation-heading-container'>
        <H3 text='Redemption Request Summary' />
        <div>
          <Span text={'Redemption process for'} />
          <Anchor
            classes={['invest-header-project-title']}
            text={opportunity?.projectName}
            href={`/opportunity/${opportunity?.projectSlug}/${opportunity?.id}`}
            variant='basic-anchor'
          />
        </div>
      </div>

      <div className='payment-upload-doc-user-wrapper withdraw-place-request'>
        <div className='payment-upload-doc-user-wrapper-left'>
          <div
            className='payment-warning-container'
            data-testid='withdraw-request-wallet-disclaimer'
          >
            <PendingIcon classes={['info-icon']} width='44px' height='44px' />
            After execution, proceeds are credited to your Credibila wallet. You can reinvest or
            withdraw funds to your bank from the wallet at any time.
          </div>

          <div
            className='payment-warning-container'
            data-testid='withdraw-request-decision-disclaimer'
          >
            <PendingIcon classes={['info-icon']} width='44px' height='44px' />
            The redemption is subject to approval decision of the fund manager. You will receive a
            notification from Credibila within 24 hours regarding your redemption confirmation and
            final redemption amount.
          </div>

          <div className='payment-button-container withdraw-place-request__cta'>
            <Button
              text='Place Redemption Request'
              handleClick={() => setShowCancellationNotice(true)}
              variant='primary-btn'
            />
          </div>
        </div>

        <div className='payment-upload-doc-user-wrapper-right'>
          <div className='payment-company-card'>
            <span className='summary-label'> {'Redemption Summary'} </span>
            <div className='company-image-container'>
              <Image source={opportunity?.projectCover} radius='r8' variant='round' />
              <span className='company-name'> {opportunity?.projectName} </span>
            </div>
            <div className='property-container'>
              <div className='property-row'>
                <span className='property-column-left' data-testid='withdrawal-amount-text'>
                  Redemption amount:
                </span>
                <span className='property-column-right' data-testid='withdrawal-amount-value'>
                  {displayDollar(
                    String(Number(withdrawalAmount) * 100),
                    opportunity?.currencyCode,
                    2,
                    true,
                  )}
                </span>
              </div>
              <div className='property-row'>
                <span className='property-column-left' data-testid='platform-withdrawal-fee-text'>
                  Dealing charges at exit:
                </span>
                <span className='property-column-right' data-testid='platform-withdrawal-fee-value'>
                  {displayDollar(
                    (PLATFORM_WITHDRAWAL_FEE * 100).toString(),
                    PLATFORM_WITHDRAWAL_FEE_CURRENCY,
                  )}
                </span>
              </div>
            </div>
            <Line />
            <div className='property-container'>
              <div className='property-row'>
                <span className='property-column-left' data-testid='total-amount-text'>
                  Net Redemption amount
                </span>
                <span className='property-column-right font-bold' data-testid='total-amount-value'>
                  {displayDollar(
                    getTotalAmount(Number(withdrawalAmount) * 100, PLATFORM_WITHDRAWAL_FEE * 100),
                    opportunity?.currencyCode,
                    2,
                    true,
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WithdrawalNoticeModal
        variant='confirm'
        isOpen={showCancellationNotice}
        isSubmitting={isSubmitting}
        onClose={() => setShowCancellationNotice(false)}
        onConfirm={handleConfirmPlaceWithdrawalRequest}
        testId='withdrawal-cancellation-notice'
      />

      <WithdrawalNoticeModal
        variant='success'
        isOpen={showSuccessNotice}
        onClose={handleDismissSuccessNotice}
        testId='withdrawal-success-notice'
      />
    </>
  )
}

export { WithdrawalRequestConfirmation }
